<script lang="ts">
    import type { IconAnimated } from "@components/material/Icon.module"
    import Icon from "@components/material/Icon.svelte"
    import { clickEnter } from "@utils/directives/accessibility"
    import { getContextMenuContext } from "./ContextMenu.svelte"
    export let icon: IconAnimated | null
    export let toggled = false
    export let cb = () => {}
    export let passToContext = true
    export let tone: "normal" | "danger" = "normal"
    let className = ""
    export { className as class }

    // Allows us to pass the exit event upwards
    const { onCallback } = getContextMenuContext()

    function debug(message: string, details?: Record<string, unknown>) {
        if (!import.meta.env.DEV) return
        console.info("[context-menu]", message, details ?? {})
    }

    function activate(event: MouseEvent) {
        const item = (event.currentTarget as HTMLElement).textContent?.trim()
        debug("item activated", { item, passToContext })
        cb()
        if (passToContext) {
            debug("item requested menu exit", { item })
            onCallback()
        }
    }
</script>

<button
    type="button"
    tabindex={0}
    on:click|stopPropagation={activate}
    on:keydown|stopPropagation={clickEnter}
    role="menuitem"
    class={`context-menu-link ${className}`}
    class:danger={tone === "danger"}
>
    {#if icon || $$slots.icon}
        <span class="context-menu-icon-slot" aria-hidden="true">
            <slot name="icon">
                {#if icon}
                    <Icon {toggled} {icon} class="context-menu-link-icon" />
                {/if}
            </slot>
        </span>
    {/if}
    <span class="context-menu-label"><slot /></span>
</button>

<style>
    .context-menu-link {
        appearance: none;
        display: flex;
        align-items: center;
        width: 100%;
        min-width: 0;
        min-height: 2.5rem;
        gap: 0.7rem;
        border: 0;
        border-bottom: 1px solid var(--context-menu-item-separator, transparent);
        border-radius: 0;
        background: var(--context-menu-item-bg, transparent);
        color: var(--context-menu-item-text, inherit);
        cursor: pointer;
        font: inherit;
        font-weight: 650;
        line-height: 1.15;
        padding: 0.65rem 0.78rem;
        text-align: left;
        transition:
            border-color 0.16s ease,
            background-color 0.16s ease,
            color 0.16s ease;
    }

    .context-menu-link:hover,
    .context-menu-link:focus-visible {
        border-bottom-color: var(
            --context-menu-item-hover-separator,
            var(--context-menu-item-separator, transparent)
        );
        background: var(--context-menu-item-hover-bg, transparent);
        color: var(--context-menu-item-hover-text, currentColor);
    }

    .context-menu-link:focus-visible {
        outline: 2px solid var(--context-menu-focus-outline, var(--color-gold-400));
        outline-offset: 2px;
    }

    .context-menu-link:active {
        border-bottom-color: var(
            --context-menu-item-active-separator,
            var(--context-menu-item-hover-separator)
        );
        background: var(--context-menu-item-active-bg, var(--context-menu-item-hover-bg));
        color: var(--context-menu-item-active-text, var(--context-menu-item-hover-text));
    }

    .context-menu-link.danger {
        border-bottom-color: var(
            --context-menu-danger-separator,
            var(--context-menu-item-separator)
        );
        background: var(--context-menu-danger-bg, var(--context-menu-item-bg));
        color: var(--context-menu-danger-text, var(--danger));
    }

    .context-menu-link.danger:hover,
    .context-menu-link.danger:focus-visible {
        border-bottom-color: var(
            --context-menu-danger-hover-separator,
            var(--context-menu-danger-separator)
        );
        background: var(--context-menu-danger-hover-bg, transparent);
        color: var(--context-menu-danger-text, var(--danger));
    }

    .context-menu-link.danger:active {
        border-bottom-color: var(
            --context-menu-danger-active-separator,
            var(--context-menu-danger-hover-separator)
        );
        background: var(--context-menu-danger-active-bg, var(--context-menu-danger-hover-bg));
        color: var(--context-menu-danger-text, var(--danger));
    }

    .context-menu-icon-slot {
        display: grid;
        place-items: center;
        flex: 0 0 auto;
        width: 1.1rem;
        min-width: 1.1rem;
        color: var(--context-menu-item-icon, currentColor);
        line-height: 1;
    }

    .context-menu-link:hover .context-menu-icon-slot,
    .context-menu-link:focus-visible .context-menu-icon-slot {
        color: currentColor;
    }

    .context-menu-link.danger .context-menu-icon-slot {
        color: currentColor;
    }

    .context-menu-label {
        display: block;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .context-menu-link :global(.context-menu-link-icon) {
        width: 1rem;
        height: 1rem;
        opacity: 0.72;
    }

    .context-menu-link:first-child {
        border-top-left-radius: calc(var(--context-menu-radius, 0.85rem) - 1px);
        border-top-right-radius: calc(var(--context-menu-radius, 0.85rem) - 1px);
    }

    .context-menu-link:last-child {
        border-bottom: 0;
        border-bottom-right-radius: calc(var(--context-menu-radius, 0.85rem) - 1px);
        border-bottom-left-radius: calc(var(--context-menu-radius, 0.85rem) - 1px);
    }
</style>
